import React, { useState } from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Alert, Typography, Avatar, Chip, Tooltip, IconButton, TablePagination } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentInd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import StaffFormModal from '../../../components/StaffFormModal';
import StaffDetailsModal from '../../../components/StaffDetailsModal';
import DeactivateStaffModal from '../../../components/DeactivateStaffModal';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { StaffService } from '../../../api/services/staffService';
import { exportClientSide } from '../../../utils/exportUtils';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';
import ReplayIcon from '@mui/icons-material/Replay';

export default function StaffDirectoryTab({
    refreshKey = 0,
    staffMembers,
    departments,
    isLoading,
    error,
    fetchStaff,
    handleSuccess,
    exportToCSV,
    setNotification,
    handleOpenAssignModal
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('All');
    const [staffList, setStaffList] = useState([]);
    const [totalStaff, setTotalStaff] = useState(0);
    const [localIsLoading, setLocalIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedStaffId, setSelectedStaffId] = useState(null);
    const [detailsTab, setDetailsTab] = useState(0);
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
    const [staffToDeactivate, setStaffToDeactivate] = useState(null);
    const [isActivatingStaff, setIsActivatingStaff] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchPaginatedStaff = async () => {
        setLocalIsLoading(true);
        try {
            const params = {
                page,
                limit: rowsPerPage,
                ...(searchTerm && { search: searchTerm }),
                ...(departmentFilter !== 'All' && { departmentId: departmentFilter })
            };
            const data = await StaffService.getStaff(params);
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setStaffList(dataArray);
            setTotalStaff(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error("Failed to fetch paginated staff:", error);
        } finally {
            setLocalIsLoading(false);
        }
    };

    React.useEffect(() => {
        fetchPaginatedStaff();
    }, [page, rowsPerPage, searchTerm, departmentFilter, refreshKey]);

    // Re-fetch directory whenever parent staffMembers updates (e.g., from "+ Add Staff" or parent actions)
    const isFirstRender = React.useRef(true);
    React.useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        fetchPaginatedStaff();
    }, [staffMembers]);

    const handleOpenForm = (staff = null) => {
        setSelectedStaff(staff);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setSelectedStaff(null);
    };

    const handleOpenDetails = (id) => {
        setDetailsTab(0);
        setSelectedStaffId(id);
        setIsDetailsOpen(true);
    };

    const handleOpenPerformanceNotes = (id) => {
        setDetailsTab(1);
        setSelectedStaffId(id);
        setIsDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setIsDetailsOpen(false);
        setSelectedStaffId(null);
        setDetailsTab(0);
    };

    const handleOpenDeactivateModal = (staff) => {
        setStaffToDeactivate(staff);
        setIsDeactivateModalOpen(true);
    };

    const handleModalSuccess = (msg, updatedStaffId = null, newStatus = null) => {
        if (updatedStaffId && newStatus) {
            setStaffList(prev => prev.map(s => (s.id === updatedStaffId || s._id === updatedStaffId) ? { ...s, status: newStatus } : s));
        }
        if (handleSuccess) handleSuccess(msg);
        fetchPaginatedStaff();
    };

    const handleDeactivateSuccess = (msg, deactivatedId) => {
        const staffId = deactivatedId || staffToDeactivate?.id || staffToDeactivate?._id;
        if (staffId) {
            setStaffList(prev => prev.map(s => (s.id === staffId || s._id === staffId) ? { ...s, status: 'Inactive' } : s));
        }
        if (handleSuccess) handleSuccess(msg);
        fetchPaginatedStaff();
    };

    const handleActivateStaff = async (staffId) => {
        setIsActivatingStaff(true);
        // Optimistic UI update so status changes instantly
        setStaffList(prev => prev.map(s => (s.id === staffId || s._id === staffId) ? { ...s, status: 'Active' } : s));
        try {
            await StaffService.updateStaff(staffId, { status: 'Active' });
            if (handleSuccess) handleSuccess('Staff member has been reactivated.');
            fetchPaginatedStaff();
        } catch (err) {
            // Revert by re-fetching on failure
            fetchPaginatedStaff();
            setNotification({ open: true, message: err.message || 'Failed to reactivate staff.', severity: 'error' });
        } finally {
            setIsActivatingStaff(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Inactive': return 'error';
            case 'On Leave': return 'warning';
            default: return 'default';
        }
    };

    const handleExport = (format) => {
        setExportModalOpen(false);
        try {
            exportClientSide(format, staffList, 'staff_directory');
            handleSuccess('Export successful.');
        } catch (error) {
            setNotification({ open: true, message: error.message || 'Export failed.', severity: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={standardFilterContainerSx}>
                <TextField 
                    size="small" 
                    placeholder="Search staff by name or ID..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={standardSearchInputSx} 
                    InputProps={{ startAdornment: <SearchIcon sx={{ color: '#98a0aa', fontSize: 18, mr: 1 }} /> }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select 
                            displayEmpty
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            <MenuItem value="All">All Depts</MenuItem>
                            {departments.map(dept => (
                                <MenuItem key={dept.id || dept._id} value={dept.id || dept._id}>{dept.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(searchTerm !== '' || departmentFilter !== 'All') && (
                        <Button 
                            variant="outlined" 
                            startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} 
                            sx={standardResetButtonSx}
                            onClick={() => { setSearchTerm(''); setDepartmentFilter('All'); }}
                        >
                            Reset
                        </Button>
                    )}
                    <Button 
                        variant="outlined" 
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                        onClick={() => setExportModalOpen(true)} 
                        sx={standardSecondaryButtonSx}
                    >
                        Export
                    </Button>
                </Box>
                <ExportFormatModal
                    open={exportModalOpen}
                    onClose={() => setExportModalOpen(false)}
                    onExport={handleExport}
                    isExporting={false}
                    title="Export Staff Directory"
                />
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Staff Member</TableCell>
                            <TableCell>Employee ID</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Employment</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Duty Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {localIsLoading || isLoading ? (
                            [...Array(5)].map((_, index) => (
                                <TableRow key={`skeleton-${index}`}>
                                    <TableCell colSpan={7}><Skeleton variant="rectangular" height={40} sx={{ my: 1, borderRadius: 1 }} /></TableCell>
                                </TableRow>
                            ))
                        ) : error ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    <Alert severity="error" variant="outlined">{error}</Alert>
                                </TableCell>
                            </TableRow>
                        ) : staffList.length > 0 ? (
                            staffList
                                .map((s) => (
                                    <TableRow key={s.id || s._id} hover>
                                        <TableCell>
                                            <Box 
                                                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                                                onClick={() => handleOpenDetails(s.id || s._id)}
                                            >
                                                <Avatar sx={{ width: 32, height: 32, fontSize: '0.9rem' }}>
                                                    {(s.name || s.fullName || 'S').charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ '&:hover': { color: 'primary.main' } }}>
                                                        {s.fullName || s.name || 'Unknown Staff'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">{s.designation || 'Staff'}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{s.employeeId || 'N/A'}</TableCell>
                                        <TableCell>{s.department?.name || s.department || 'N/A'}</TableCell>
                                        <TableCell>{s.employmentType || s.type || 'Full-time'}</TableCell>
                                        <TableCell><Chip label={s.status || 'Active'} size="small" color={getStatusColor(s.status || 'Active')} variant="outlined" /></TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={s.onDuty ? "On Duty" : "Off Duty"} 
                                                size="small" 
                                                color={s.onDuty ? "success" : "default"} 
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Assign Shift">
                                                <IconButton 
                                                    size="small" 
                                                    color="info"
                                                    onClick={() => handleOpenAssignModal(s.id || s._id)}
                                                >
                                                    <AssignmentIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View Details">
                                                <IconButton size="small" onClick={() => handleOpenDetails(s.id || s._id)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <IconButton 
                                                    size="small" 
                                                    color="primary"
                                                    onClick={() => handleOpenForm(s)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {s.status === 'Active' ? (
                                                <Tooltip title="Deactivate">
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={() => handleOpenDeactivateModal(s)}
                                                    >
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Reactivate">
                                                    <IconButton 
                                                        size="small" 
                                                        color="primary"
                                                        disabled={isActivatingStaff}
                                                        onClick={() => handleActivateStaff(s.id || s._id)}
                                                    >
                                                        <CheckCircleIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Performance Notes">
                                                <IconButton 
                                                    size="small" 
                                                    color="secondary"
                                                    onClick={() => handleOpenPerformanceNotes(s.id || s._id)}
                                                >
                                                    <NoteAltIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body2" color="text.secondary">No staff members found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalStaff}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>

            <StaffFormModal 
                open={isFormOpen} 
                onClose={handleCloseForm} 
                onSuccess={handleModalSuccess} 
                staffData={selectedStaff}
            />

            <StaffDetailsModal 
                open={isDetailsOpen} 
                onClose={handleCloseDetails} 
                staffId={selectedStaffId} 
                onEdit={handleOpenForm}
                initialTab={detailsTab}
                onSuccess={handleModalSuccess}
            />

            <DeactivateStaffModal 
                open={isDeactivateModalOpen}
                onClose={() => setIsDeactivateModalOpen(false)}
                staff={staffToDeactivate}
                staffData={staffToDeactivate}
                onSuccess={handleDeactivateSuccess}
            />
        </Box>
    );
}
